// ConnectorDetailHeader — the top of the Connector Detail page.
//
// Pulled out of ConnectorDetailPage so the page file (still ~2 kloc of
// per-type form branches) can stay focused on its forms while the header
// gets to be the dense, deliberate "system status" element it should be.
//
// What lives here:
//   • Brand icon, editable name, type label, age caption
//   • Status block (pill + last-verified copy + inline error)
//   • Top-right action cluster (Test connection / Re-authenticate / Edit /
//     Delete) — Test and Re-auth show only when relevant
//   • Stats strip with sync cadence sparkline (T3)
//
// We intentionally do NOT touch any form-edit state from here — name
// editing remains the page's concern (it owns `editingName`) so the
// existing rename plumbing keeps working unchanged.

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshRounded from '@mui/icons-material/Refresh';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import LockResetRounded from '@mui/icons-material/LockResetRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import SecurityRounded from '@mui/icons-material/SecurityRounded';
import { ConnectorIcon, prettyConnectorType } from '../../utils/connectorMeta';
import { formatRelativeTime, formatAbsolute } from '../../utils/date';
import SyncSparkline, { SyncSparklinePoint } from './SyncSparkline';

export interface ConnectorDetailHeaderProps {
  data: any;

  // Name editing — owned by parent.
  editingName: boolean;
  editedName: string;
  savingName: boolean;
  setEditedName: (v: string) => void;
  onStartEditName: () => void;
  onCancelEditName: () => void;
  onSaveName: () => void;

  // Actions
  onDelete: () => void;
  onTestConnection: () => void;
  onReauthenticate?: () => void;
  testing: boolean;
  reauthenticating: boolean;

  /** Set on the client whenever a Test Connection round-trip resolves. */
  lastTestedAt: number | null;
  /**
   * Result of the most recent Test Connection probe. The probe is a
   * read-only health check on the server, so we surface its outcome here
   * separately from the persisted `data.status`. That way a user-paused
   * connector stays paused even when a probe succeeds, but the operator
   * still sees "Connection successful just now" feedback.
   */
  lastTestResult: { success: boolean; errorMessage: string | null } | null;

  // Stats inputs
  selectedCount: number;
  selectedItemLabel: string;
  syncPoints: SyncSparklinePoint[];
}

function statusVisuals(status: string) {
  const s = (status || '').toLowerCase();
  if (s === 'active') {
    return {
      label: 'Connected',
      color: 'success' as const,
      Icon: CheckCircleRounded,
      iconColor: 'success.main',
      desc: 'Connection verified',
    };
  }
  if (s === 'inactive') {
    return {
      label: 'Inactive',
      color: 'default' as const,
      Icon: CircleOutlined,
      iconColor: 'text.disabled',
      desc: 'Connector is paused',
    };
  }
  return {
    label: 'Connection failed',
    color: 'error' as const,
    Icon: ErrorRounded,
    iconColor: 'error.main',
    desc: 'Last connection attempt failed',
  };
}

function isOAuth(data: any): boolean {
  return !!(data?.config?.oauth_access_token || data?.config?.oauth_refresh_token);
}

export default function ConnectorDetailHeader({
  data,
  editingName,
  editedName,
  savingName,
  setEditedName,
  onStartEditName,
  onCancelEditName,
  onSaveName,
  onDelete,
  onTestConnection,
  onReauthenticate,
  testing,
  reauthenticating,
  lastTestedAt,
  lastTestResult,
  selectedCount,
  selectedItemLabel,
  syncPoints,
}: ConnectorDetailHeaderProps) {
  const status = statusVisuals(data?.status);
  const StatusIcon = status.Icon;

  const lastSyncLabel = data?.last_schema_fetch ? formatRelativeTime(data.last_schema_fetch) : null;
  const createdLabel = data?.created_at ? formatRelativeTime(data.created_at) : null;
  const verifiedLabel = lastTestedAt ? formatRelativeTime(new Date(lastTestedAt)) : null;
  const oauth = isOAuth(data);

  return (
    <Card sx={{ mb: 2.5, overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        {/* ── Row 1: identity + actions ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Identity block */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flex: 1, minWidth: 0, width: '100%' }}>
            <Box
              sx={(t) => ({
                width: 52,
                height: 52,
                bgcolor: t.tokens.brand.primarySoft,
                borderRadius: `${t.ds.radius.md}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              <ConnectorIcon type={data?.type} size={28} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {/* Name + edit */}
              {editingName ? (
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexWrap: 'wrap' }}>
                  <TextField
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    size="small"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSaveName();
                      else if (e.key === 'Escape') onCancelEditName();
                    }}
                    sx={{ minWidth: { xs: 180, sm: 320 } }}
                  />
                  <IconButton
                    size="small"
                    onClick={onSaveName}
                    disabled={savingName || !editedName.trim()}
                    color="primary"
                    aria-label="Save name"
                  >
                    {savingName ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                  <IconButton size="small" onClick={onCancelEditName} disabled={savingName} aria-label="Cancel name edit">
                    <CancelIcon />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.25rem', sm: '1.625rem' },
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      minWidth: 0,
                    }}
                  >
                    {data?.name || 'Unnamed Connector'}
                  </Typography>
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={onStartEditName} aria-label="Edit connector name" sx={{ ml: -0.25 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}

              {/* Sub-line: type + status pill + verified copy */}
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
                >
                  {prettyConnectorType(data?.type)}
                </Typography>

                <Box
                  sx={(t) => ({
                    width: 3,
                    height: 3,
                    borderRadius: '50%',
                    bgcolor: t.tokens.border.default,
                  })}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StatusIcon sx={{ fontSize: 16, color: status.iconColor }} />
                  <Typography
                    variant="body2"
                    sx={(t) => ({
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color:
                        status.color === 'success'
                          ? t.tokens.semantic.success
                          : status.color === 'error'
                          ? t.tokens.semantic.danger
                          : t.tokens.text.secondary,
                    })}
                  >
                    {status.label}
                  </Typography>
                </Box>

                {verifiedLabel && lastTestResult && (
                  <Tooltip
                    title={
                      lastTestResult.success
                        ? 'Last connection probe succeeded'
                        : lastTestResult.errorMessage || 'Last connection probe failed'
                    }
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={(t) => ({
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: lastTestResult.success ? t.tokens.semantic.success : t.tokens.semantic.danger,
                      })}
                    >
                      · {lastTestResult.success ? 'tested' : 'last test failed'} {verifiedLabel}
                    </Typography>
                  </Tooltip>
                )}

                {createdLabel && (
                  <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>
                    · added {createdLabel}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Action cluster */}
          <Stack
            direction="row"
            gap={0.75}
            alignItems="center"
            sx={{
              flexShrink: 0,
              flexWrap: 'wrap',
              alignSelf: { xs: 'flex-end', md: 'center' },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={testing ? <CircularProgress size={14} /> : <RefreshRounded />}
              onClick={onTestConnection}
              disabled={testing}
              sx={{ textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </Button>

            {oauth && onReauthenticate && (
              <Button
                size="small"
                variant="outlined"
                startIcon={reauthenticating ? <CircularProgress size={14} /> : <LockResetRounded />}
                onClick={onReauthenticate}
                disabled={reauthenticating}
                sx={{ textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                Re-authenticate
              </Button>
            )}

            <Tooltip title="Delete connector">
              <IconButton
                color="error"
                onClick={onDelete}
                aria-label="Delete connector"
                sx={(t) => ({
                  border: `1px solid ${t.tokens.border.subtle}`,
                  borderRadius: `${t.ds.radius.sm}px`,
                  '&:hover': {
                    borderColor: t.tokens.semantic.danger,
                    bgcolor: t.tokens.semantic.dangerSoft,
                  },
                })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* ── Inline error banner when status === 'failed' ── */}
        {data?.status && data.status.toLowerCase() !== 'active' && data?.error_message && (
          <Box
            sx={(t) => ({
              mt: 2,
              px: 1.5,
              py: 1,
              borderRadius: `${t.ds.radius.sm}px`,
              border: `1px solid ${t.tokens.semantic.danger}`,
              bgcolor: t.tokens.semantic.dangerSoft,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            })}
          >
            <ErrorRounded sx={{ fontSize: 18, color: 'error.main', mt: 0.125, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'error.main' }}>
                {status.label}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.8125rem', mt: 0.25, wordBreak: 'break-word' }}
              >
                {data.error_message}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={onTestConnection}
              disabled={testing}
              sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center', whiteSpace: 'nowrap' }}
            >
              Try again
            </Button>
          </Box>
        )}

        {/* ── Row 2: stats strip ── */}
        <Box
          sx={(t) => ({
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 1.5, md: 2 },
          })}
        >
          <StatTile
            icon={<TableChartRounded sx={{ fontSize: 16 }} />}
            label={selectedItemLabel}
            value={selectedCount > 0 ? selectedCount.toLocaleString() : '—'}
            caption={selectedCount === 0 ? 'Nothing selected' : `Selected for sync`}
          />
          <StatTile
            icon={<HistoryRounded sx={{ fontSize: 16 }} />}
            label="Last schema fetch"
            value={lastSyncLabel || '—'}
            caption={data?.last_schema_fetch ? formatAbsolute(data.last_schema_fetch) : 'Never fetched'}
          />
          <StatTile
            icon={<AccountTreeRounded sx={{ fontSize: 16 }} />}
            label="Recent syncs"
            value={
              <Box sx={{ pt: 0.25 }}>
                <SyncSparkline points={syncPoints} cap={7} showSummary />
              </Box>
            }
            caption={syncPoints.length === 0 ? 'No data sync runs yet' : 'Last 7 runs'}
          />
          <StatTile
            icon={<SecurityRounded sx={{ fontSize: 16 }} />}
            label="Authentication"
            value={oauth ? 'OAuth' : data?.config?.service_account_email ? 'Service account' : 'Credentials'}
            // Intentionally do NOT surface the access-token expiry here — it
            // auto-refreshes on every API call and showing "Expired N days ago"
            // for a token that's transparently renewed is alarming, not useful.
            // Re-authenticate (top-right action) is the only state that matters
            // and the connector status badge already telegraphs that.
            caption={oauth ? 'Auto-refreshed' : 'Stored securely'}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

function StatTile({
  icon,
  label,
  value,
  caption,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  caption?: string;
  tooltip?: string;
}) {
  const content = (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" gap={0.625} sx={{ color: 'text.secondary', mb: 0.5 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
      </Stack>
      {typeof value === 'string' ? (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>
          {value}
        </Typography>
      ) : (
        value
      )}
      {caption && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <Box>{content}</Box>
    </Tooltip>
  ) : (
    content
  );
}
